import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, Trash2, LogOut } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface OrderItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    code: string;
    unit: string;
  };
}

interface Order {
  id: string;
  order_number: string;
  created_at: string;
  status: string;
  responsible_name: string;
  construction_name: string;
  reject_reason?: string;
  departments: {
    id: string;
    name: string;
    acronym: string;
  }[];
  order_items: OrderItem[];
  user_id: string;
  observation?: string;
}

interface Department {
  id: string;
  name: string;
}

const statusMap = {
  pending: { label: "Pendente", color: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  approved: { label: "Aprovado", color: "bg-green-50 text-green-700 border-green-200" },
  rejected: { label: "Rejeitado", color: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Concluído", color: "bg-blue-50 text-blue-700 border-blue-200" }
};

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      if (!id) return;

      console.log('Buscando detalhes do pedido:', id);

      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          department:departments (*),
          order_items (
            id,
            quantity,
            product:products (
              id,
              name,
              code,
              unit
            )
          ),
          reject_reason
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar pedido:', error);
        throw error;
      }

      console.log('Dados do pedido:', data);

      const formattedOrder = {
        ...data,
        departments: [data.department],
        order_items: data.order_items.map((item: any) => ({
          id: item.id,
          quantity: item.quantity,
          product: item.product
        }))
      };

      console.log('Pedido formatado:', formattedOrder);
      setOrder(formattedOrder);
    } catch (error) {
      console.error("Erro ao buscar detalhes do pedido:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os detalhes do pedido",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!order) return;

    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(16);
    doc.text(`Pedido #${order.order_number}`, 14, 20);

    // Informações do pedido
    doc.setFontSize(12);
    doc.text(`Solicitante: ${order.responsible_name}`, 14, 30);
    doc.text(`Departamento: ${order.departments[0].name}`, 14, 37);
    doc.text(`Obra: ${order.construction_name}`, 14, 44);
    doc.text(`Data: ${new Date(order.created_at).toLocaleDateString('pt-BR')}`, 14, 51);
    doc.text(`Status: ${statusMap[order.status as keyof typeof statusMap].label}`, 14, 58);

    // Tabela de produtos
    const tableData = order.order_items.map(item => [
      item.product.name,
      item.product.code,
      item.product.unit,
      item.quantity.toString()
    ]);

    autoTable(doc, {
      head: [['Produto', 'Código', 'Unidade', 'Quantidade']],
      body: tableData,
      startY: 65,
    });

    doc.save(`pedido-${order.order_number}.pdf`);
    
    toast({
      title: "PDF gerado com sucesso",
      description: "O arquivo foi baixado para o seu computador",
    });
  };

  const handleDelete = async () => {
    if (!order) return;

    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', order.id);

      if (error) throw error;

      toast({
        title: "Pedido excluído",
        description: "O pedido foi excluído com sucesso",
      });

      navigate('/orders');
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o pedido",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Pedido não encontrado</div>
        </div>
      </div>
    );
  }

  const status = statusMap[order.status as keyof typeof statusMap] || statusMap.pending;

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Botão Voltar e Ações */}
      <div className="flex justify-between items-center mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        
        <div className="flex gap-2">
          {order.status === 'approved' && (
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />
              Gerar Comprovante
            </Button>
          )}
          
          <Button
            variant="destructive"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Excluir
          </Button>
        </div>
      </div>

      {/* Informações Principais */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Pedido #{order.order_number}</CardTitle>
            <Badge className={
              order.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
              order.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
              order.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
              order.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''
            }>
              {order.status === 'approved' && 'Aprovado'}
              {order.status === 'pending' && 'Pendente'}
              {order.status === 'rejected' && 'Rejeitado'}
              {order.status === 'completed' && 'Concluído'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Informações do Pedido</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Solicitante:</span> {order.responsible_name}</p>
                <p><span className="font-medium">Departamento:</span> {order.departments[0].name}</p>
                <p><span className="font-medium">Obra:</span> {order.construction_name}</p>
                <p><span className="font-medium">Data:</span> {new Date(order.created_at).toLocaleDateString('pt-BR')}</p>
                {order.observation && (
                  <p><span className="font-medium">Observação:</span> {order.observation}</p>
                )}
                {order.status === 'rejected' && order.reject_reason && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="font-medium text-red-700 mb-1">Motivo da Rejeição:</p>
                    <p className="text-red-600">{order.reject_reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Produtos */}
      <Card>
        <CardHeader>
          <CardTitle>Produtos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">Produto</th>
                  <th scope="col" className="px-6 py-3">Código</th>
                  <th scope="col" className="px-6 py-3">Unidade</th>
                  <th scope="col" className="px-6 py-3">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {order.order_items?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center">
                      Nenhum produto encontrado
                    </td>
                  </tr>
                ) : (
                  order.order_items?.map((item) => {
                    console.log('Renderizando item:', item);
                    return (
                      <tr key={item.id} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-4">{item.product?.name || 'N/A'}</td>
                        <td className="px-6 py-4">{item.product?.code || 'N/A'}</td>
                        <td className="px-6 py-4">{item.product?.unit || 'N/A'}</td>
                        <td className="px-6 py-4">{item.quantity}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Observações */}
      {order.observation && (
        <Card>
          <CardHeader>
            <CardTitle>Observações</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{order.observation}</p>
          </CardContent>
        </Card>
      )}

      {/* Diálogo de confirmação de exclusão */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o pedido #{order?.order_number}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
